import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { Template } from '../../types/template';
import { useTemplate } from '../../contexts/TemplateContext';
import TemplatePreviewModal from './TemplatePreviewModal';
import ConfirmationDialog from './ConfirmationDialog';
import { IGlobalTemplate, IUserTemplatesListResult, useDeleteTemplate, useDeleteEditorTemplate } from 'core';
import { SkeletonSwitch } from 'skeleton';
import { TemplateCardGridSkeleton } from 'skeleton/template/TemplateSkeleton';
import { useNavigate } from 'react-router-dom';
import { toast } from 'components/ui/toast';
import NewTemplateCard from './template-card/NewTemplateCard';
import {
    Eye, Copy, Trash2, MoreHorizontal,
    Search, ChevronDown, LayoutGrid, List, LayoutTemplate, SlidersHorizontal,
} from 'lucide-react';

const useResponsiveColumns = () => {
    const [cols, setCols] = useState(() => {
        if (typeof window !== 'undefined') {
            if (window.innerWidth >= 1280) return 5;
            if (window.innerWidth >= 1024) return 4;
            if (window.innerWidth >= 768) return 3;
            return 2;
        }
        return 5;
    });

    useEffect(() => {
        const updateCols = () => {
            if (window.innerWidth >= 1280) setCols(5);
            else if (window.innerWidth >= 1024) setCols(4);
            else if (window.innerWidth >= 768) setCols(3);
            else setCols(2);
        };
        window.addEventListener('resize', updateCols);
        return () => window.removeEventListener('resize', updateCols);
    }, []);

    return cols;
};

const MasonryGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const MathCols = useResponsiveColumns();
    const childrenArray = React.Children.toArray(children);
    const columns: React.ReactNode[][] = Array.from({ length: MathCols }, () => []);

    // Distribute children left-to-right into columns
    childrenArray.forEach((child, index) => {
        columns[index % MathCols].push(child);
    });

    return (
        <div className="flex items-start gap-3 w-full pb-5">
            {columns.map((colChildren, i) => (
                <div key={i} className="flex-1 flex flex-col gap-3 min-w-0">
                    {colChildren}
                </div>
            ))}
        </div>
    );
};

interface TemplateGridProps {
    templates: Template[];
    viewMode: 'grid' | 'list';
    favorites: Set<string>;
    onToggleFavorite: (templateId: string) => void;
    userTemplates?: IUserTemplatesListResult;
    globalTemplates?: IGlobalTemplate[];
    loadingUserTemplates?: boolean;
    loadingGlobalTemplates?: boolean;
}

const toDate = (input?: string | Date | null): Date | null => {
    if (!input) return null;
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
    const str = String(input).trim();
    if (!str) return null;
    const isoish = str.includes('T') ? str : str.replace(' ', 'T');
    const d = new Date(isoish);
    return isNaN(d.getTime()) ? null : d;
};

const formatDate = (input?: Date | null): string => {
    if (!input) return '—';
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - input.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays < 7) return `${diffDays - 1}d ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)}w ago`;
    return input.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const useClickOutside = (
    ref: React.RefObject<HTMLDivElement | null>,
    callback: () => void,
    active: boolean
) => {
    useEffect(() => {
        if (!active) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) callback();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [active, callback, ref]);
};

const MoreMenu: React.FC<{
    onPreview: () => void;
    onDuplicate: () => void;
    onDelete?: () => void;
    isDeletable?: boolean;
    onOpenChange?: (open: boolean) => void;
}> = ({ onPreview, onDuplicate, onDelete, isDeletable, onOpenChange }) => {
    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);
    const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);

    const close = useCallback(() => {
        setOpen(false);
        setPos(null);
        onOpenChange?.(false);
    }, [onOpenChange]);

    const toggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (open) { close(); return; }
        const rect = btnRef.current?.getBoundingClientRect();
        if (!rect) return;
        const MENU_H = 160;
        const spaceBelow = window.innerHeight - rect.bottom;
        setPos(
            spaceBelow >= MENU_H
                ? { top: rect.bottom + 4, right: window.innerWidth - rect.right }
                : { bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right }
        );
        setOpen(true);
        onOpenChange?.(true);
    }, [open, close, onOpenChange]);

    useEffect(() => {
        if (!open) return;
        const dismiss = () => close();
        window.addEventListener('scroll', dismiss, true);
        window.addEventListener('resize', dismiss);
        return () => {
            window.removeEventListener('scroll', dismiss, true);
            window.removeEventListener('resize', dismiss);
        };
    }, [open, close]);

    return (
        <>
            <button
                ref={btnRef}
                onClick={toggle}
                className="w-8 h-8 flex items-center justify-center rounded-lg
          text-content-subtle dark:text-gray-500
          hover:text-gray-700 dark:hover:text-gray-200
          hover:bg-gray-100 dark:hover:bg-dark-700
          border border-transparent hover:border-line dark:hover:border-dark-600
          transition-all"
                title="More actions"
            >
                <MoreHorizontal className="w-4 h-4" />
            </button>

            {open && pos && createPortal(
                <>
                    <div className="fixed inset-0 z-[9990]" onClick={(e) => { e.stopPropagation(); close(); }} />
                    <div
                        className="fixed z-[9991] w-44 bg-surface dark:bg-dark-800 rounded-xl shadow-xl border border-line dark:border-dark-700 py-1.5 overflow-hidden"
                        style={{ top: pos.top ?? 'auto', bottom: pos.bottom ?? 'auto', right: pos.right, animation: 'fadeIn 0.12s ease both' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={(e) => { e.stopPropagation(); onPreview(); close(); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] font-medium
              text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors text-left"
                        >
                            <Eye className="w-3.5 h-3.5 text-content-subtle dark:text-gray-500" />
                            Preview
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onDuplicate(); close(); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] font-medium
              text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors text-left"
                        >
                            <Copy className="w-3.5 h-3.5 text-content-subtle dark:text-gray-500" />
                            Duplicate
                        </button>
                        {isDeletable && onDelete && (
                            <>
                                <div className="mx-2 my-1 border-t border-line-subtle dark:border-dark-700" />
                                <button
                                    onClick={(e) => { e.stopPropagation(); onDelete(); close(); }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] font-medium
                  text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors text-left"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Delete
                                </button>
                            </>
                        )}
                    </div>
                </>,
                document.body
            )}
        </>
    );
};

const Thumbnail: React.FC<{ src?: string; name: string }> = ({ src, name }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const imgSrc = (src && String(src).trim()) || '';

    return (
        <div className="w-[72px] h-[48px] rounded-xl overflow-hidden flex-shrink-0
      bg-gray-100 dark:bg-dark-700
      border border-line dark:border-dark-600
      flex items-center justify-center shadow-sm">
            {!error && imgSrc ? (
                <img
                    src={imgSrc}
                    alt={name}
                    className={`w-full h-full object-cover object-top transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                    loading="lazy"
                    onLoad={() => setLoaded(true)}
                    onError={() => setError(true)}
                />
            ) : (
                <svg className="w-4.5 h-4.5 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
            )}
        </div>
    );
};

const TemplateGrid: React.FC<TemplateGridProps> = ({
    favorites,
    onToggleFavorite,
    userTemplates,
    globalTemplates,
    loadingUserTemplates,
    loadingGlobalTemplates,
}) => {
    const navigate = useNavigate();
    const { actions } = useTemplate();
    const deleteTemplateMutation = useDeleteTemplate();
    const deleteEditorTemplateMutation = useDeleteEditorTemplate();
    const [previewTemplate, setPreviewTemplate] = useState<any | null>(null);
    const [previewIsGlobal, setPreviewIsGlobal] = useState<boolean>(false);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        type: 'delete' | 'duplicate' | null;
        template: any | null;
        templateId?: string | number;
        templateType?: 'legacy' | 'editor';
    }>({ isOpen: false, type: null, template: null });

    const [activeView, setActiveView] = useState<'grid' | 'list'>('grid');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<'newest' | 'oldest' | 'name'>('newest');
    const [showSort, setShowSort] = useState(false);
    const sortRef = useRef<HTMLDivElement>(null);
    useClickOutside(sortRef, () => setShowSort(false), showSort);

    // ── Handlers ─────────────────────────────────────────────────────────
    const handlePreview = (template: any, isGlobal: boolean = false) => {
        setPreviewTemplate(template);
        setPreviewIsGlobal(isGlobal);
    };

    const _handleEdit = (template: Template) => {
        actions.setCurrentTemplate(template);
    };

    const handleDuplicate = (template: Template) => {
        setConfirmDialog({ isOpen: true, type: 'duplicate', template });
    };

    const handleDeleteUserTemplate = (template: any, templateId: string | number) => {
        const templateType: 'legacy' | 'editor' = template?.templateType === 'editor' ? 'editor' : 'legacy';
        setConfirmDialog({ isOpen: true, type: 'delete', template, templateId, templateType });
    };

    const handleConfirmAction = async () => {
        if (!confirmDialog.template) return;
        if (confirmDialog.type === 'delete' && confirmDialog.templateId) {
            try {
                if (confirmDialog.templateType === 'editor') {
                    await deleteEditorTemplateMutation.mutateAsync(confirmDialog.templateId);
                } else {
                    await deleteTemplateMutation.mutateAsync(confirmDialog.templateId);
                }
                toast.success('Template moved to trash');
                setConfirmDialog({ isOpen: false, type: null, template: null });
            } catch (error: any) {
                const responseData = error?.response?.data;
                const errorMessage =
                    (typeof responseData === 'string' && responseData.trim())
                    || responseData?.message
                    || error?.message;
                toast.error(errorMessage);
                setConfirmDialog({ isOpen: false, type: null, template: null });
            }
        } else if (confirmDialog.type === 'duplicate') {
            setConfirmDialog({ isOpen: false, type: null, template: null });
        }
    };

    const handleCancelAction = () => {
        setConfirmDialog({ isOpen: false, type: null, template: null });
    };

    // ── Filter + sort ────────────────────────────────────────────────────
    const filterAndSort = <T extends any>(
        items: T[],
        search: string,
        sort: string,
        getFields: (item: T) => { name: string; date: Date | null }
    ) => {
        let filtered = items;
        if (search.trim()) {
            const q = search.toLowerCase();
            filtered = filtered.filter((item) => getFields(item).name.toLowerCase().includes(q));
        }
        return [...filtered].sort((a, b) => {
            const fa = getFields(a);
            const fb = getFields(b);
            if (sort === 'name') return fa.name.localeCompare(fb.name);
            if (sort === 'oldest') return (fa.date?.getTime() || 0) - (fb.date?.getTime() || 0);
            return (fb.date?.getTime() || 0) - (fa.date?.getTime() || 0);
        });
    };

    const processedUserTemplates = useMemo(() => {
        const items = userTemplates?.items ?? [];
        return filterAndSort(items, search, sort, (t: any) => ({
            name: t.name || '',
            date: toDate(t.modifiedDate),
        }));
    }, [userTemplates, search, sort]);

    const processedGlobalTemplates = useMemo(() => {
        const items = globalTemplates ?? [];
        return filterAndSort(items, search, sort, (t: any) => ({
            name: t.name || '',
            date: toDate(t.modifiedDate),
        }));
    }, [globalTemplates, search, sort]);

    // ── TemplateRow (must stay inlined — no remount of search) ───────────
    const TemplateRow: React.FC<{
        template: any;
        onClick: () => void;
        onEdit: () => void;
        onPreview: () => void;
        onDuplicate: () => void;
        onDelete?: () => void;
        isDeletable: boolean;
        isLast?: boolean;
    }> = ({ template, onClick, onEdit: _onEdit, onPreview, onDuplicate, onDelete, isDeletable }) => {
        const thumbSrc = template.thumbnail || template.thumbnail_url || '';
        const tag = template.tag || template.category || '';
        const dateObj = toDate(template.modifiedDate);
        const [menuOpen, setMenuOpen] = useState(false);

        return (
            <div
                className="relative flex items-center gap-4 px-4 py-3 cursor-pointer group/row
          bg-surface dark:bg-dark-800
          border border-line dark:border-dark-700
          rounded-xl shadow-sm
          hover:shadow-md hover:-translate-y-[1px]
          hover:border-blue-300 dark:hover:border-blue-700/60
          transition-all duration-200"
                style={{ zIndex: menuOpen ? 10 : 'auto' }}
                onClick={onClick}
            >
                {/* Left accent line */}
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-gradient-to-b from-blue-500 to-indigo-500 opacity-0 group-hover/row:opacity-100 transition-opacity" />

                {/* Thumbnail */}
                <Thumbnail src={thumbSrc} name={template.name} />

                {/* Name + tag */}
                <div className="min-w-0 w-36 sm:w-56 flex-shrink-0">
                    <h3 className="text-ui font-semibold text-content dark:text-white truncate leading-tight
            group-hover/row:text-blue-600 dark:group-hover/row:text-blue-400 transition-colors">
                        {template.name}
                    </h3>
                    {tag && (
                        <p className="text-micro text-content-muted dark:text-gray-400 truncate mt-0.5 leading-tight">
                            {tag}
                        </p>
                    )}
                </div>

                <div className="flex-1" />

                {/* Category badge */}
                <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-lg text-micro font-semibold
          bg-gray-100 dark:bg-dark-700 text-content-secondary dark:text-gray-400
          border border-line dark:border-dark-600
          whitespace-nowrap flex-shrink-0">
                    {tag || 'Custom'}
                </span>

                {/* Date */}
                <span className="text-micro text-content-muted dark:text-gray-400 whitespace-nowrap flex-shrink-0 w-20 text-right tabular-nums hidden md:block">
                    {formatDate(dateObj)}
                </span>

                {/* More menu */}
                <div onClick={(e) => e.stopPropagation()}>
                    <MoreMenu
                        onPreview={onPreview}
                        onDuplicate={onDuplicate}
                        onDelete={onDelete}
                        isDeletable={isDeletable}
                        onOpenChange={setMenuOpen}
                    />
                </div>
            </div>
        );
    };

    // ── renderEmpty ──────────────────────────────────────────────────────
    const renderEmpty = (msg: string) => (
        <div className="flex flex-col items-center justify-center py-14 px-6 text-center
      bg-surface dark:bg-dark-800
      border border-line dark:border-dark-700
      rounded-xl shadow-sm">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20
        rounded-xl flex items-center justify-center mb-3 border border-blue-100 dark:border-blue-900/30">
                <LayoutTemplate className="w-6 h-6 text-blue-500 dark:text-blue-400" />
            </div>
            <h3 className="text-[14px] font-bold text-content dark:text-white mb-1">{msg}</h3>
            <p className="text-[12px] text-content-muted dark:text-gray-400 max-w-xs">
                {search ? 'Try a different search term or clear your filter.' : 'Create your first template to get started.'}
            </p>
        </div>
    );

    // ── Shared toolbar (inlined — search must never remount) ─────────────
    const toolbar = (
        <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle dark:text-gray-500 pointer-events-none" />
                <input
                    id="template-search-input"
                    type="text"
                    placeholder="Search templates…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); }}
                    className="w-40 sm:w-52 pl-8 pr-3 h-9 text-[13px]
            bg-surface dark:bg-dark-800
            border border-line dark:border-dark-700
            rounded-xl text-gray-800 dark:text-gray-100
            placeholder-gray-400 dark:placeholder-gray-500
            focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 dark:focus:border-blue-500
            hover:border-line-strong dark:hover:border-dark-600
            shadow-sm transition-all"
                />
            </div>

            {/* Sort dropdown */}
            <div className="relative" ref={sortRef}>
                <button
                    onClick={() => setShowSort(!showSort)}
                    className="h-9 flex items-center gap-1.5 px-3 text-[13px] font-medium
            bg-surface dark:bg-dark-800
            text-content-secondary dark:text-gray-300
            rounded-xl border border-line dark:border-dark-700
            hover:bg-gray-50 dark:hover:bg-dark-700
            hover:border-line-strong dark:hover:border-dark-600
            shadow-sm transition-all"
                >
                    <SlidersHorizontal className="w-3.5 h-3.5 text-content-subtle dark:text-gray-500" />
                    Sort
                    <ChevronDown className="w-3 h-3 text-content-subtle" />
                </button>
                {showSort && (
                    <div className="absolute right-0 top-full mt-1.5 w-44
            bg-surface dark:bg-dark-800
            rounded-xl shadow-xl border border-line dark:border-dark-700
            py-1.5 z-30 overflow-hidden">
                        {([
                            { value: 'newest', label: 'Newest first' },
                            { value: 'oldest', label: 'Oldest first' },
                            { value: 'name', label: 'Name A–Z' },
                        ] as const).map((opt) => (
                            <button
                                key={opt.value}
                                onClick={() => { setSort(opt.value); setShowSort(false); }}
                                className={`w-full px-3 py-2 text-left text-[12px] font-medium transition-colors
                  ${sort === opt.value
                                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/15'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
                                    }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* View toggle pill */}
            <div className="flex items-center bg-gray-100 dark:bg-dark-700 rounded-xl p-1 gap-0.5 border border-line dark:border-dark-600">
                <button
                    onClick={() => setActiveView('grid')}
                    title="Grid view"
                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all
            ${activeView === 'grid'
                            ? 'bg-surface dark:bg-dark-600 text-content dark:text-white shadow-sm'
                            : 'text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                >
                    <LayoutGrid className="w-3.5 h-3.5" />
                </button>
                <button
                    onClick={() => setActiveView('list')}
                    title="List view"
                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all
            ${activeView === 'list'
                            ? 'bg-surface dark:bg-dark-600 text-content dark:text-white shadow-sm'
                            : 'text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                >
                    <List className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );

    return (
        <>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-10 pt-3">
                <div className="flex items-center gap-2.5">
                    <h2 className="text-[17px] font-bold text-content dark:text-white tracking-tight">
                        Your Templates
                    </h2>
                    <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-md
              text-[11px] font-bold
              bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400
              border border-blue-100 dark:border-blue-900/40">
                        {processedUserTemplates.length}
                    </span>
                </div>
                {toolbar}
            </div>
            <SkeletonSwitch isLoading={loadingUserTemplates} skeleton={<TemplateCardGridSkeleton count={4} />}>
                {processedUserTemplates.length === 0
                    ? renderEmpty(search ? 'No templates match your search' : 'No templates yet')
                    : activeView === 'grid'
                        ? (
                            <MasonryGrid>
                                {processedUserTemplates.map((template: any) => (
                                    <NewTemplateCard
                                        key={template.id}
                                        template={template}
                                        isFavorite={favorites.has(String(template.id))}
                                        onToggleFavorite={() => onToggleFavorite(String(template.id))}
                                        onPreview={() => handlePreview(template, false)}
                                        onEdit={() => navigate(`/templates/editor/${template.id}`)}
                                        onDuplicate={() => handleDuplicate(template)}
                                        onDelete={() => handleDeleteUserTemplate(template, template.id)}
                                        isDeletable={true}
                                    />
                                ))}
                            </MasonryGrid>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {processedUserTemplates.map((template: any) => (
                                    <TemplateRow
                                        key={template.id}
                                        template={template}
                                        onClick={() => navigate(`/templates/editor/${template.id}`)}
                                        onEdit={() => navigate(`/templates/editor/${template.id}`)}
                                        onPreview={() => handlePreview(template, false)}
                                        onDuplicate={() => { }}
                                        onDelete={() => handleDeleteUserTemplate(template, template.id)}
                                        isDeletable={true}
                                    />
                                ))}
                            </div>
                        )
                }
            </SkeletonSwitch>


            {/* ─── TEMPLATE LIBRARY ─── */}
            <div className="flex items-center gap-2.5 mb-4">
                <h2 className="text-[17px] font-bold text-content dark:text-white tracking-tight">
                    Template Library
                </h2>
                <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-md
            text-[11px] font-bold
            bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400
            border border-violet-100 dark:border-violet-900/40">
                    {processedGlobalTemplates.length}
                </span>
            </div>

            <SkeletonSwitch isLoading={loadingGlobalTemplates} skeleton={<TemplateCardGridSkeleton count={4} />}>
                {processedGlobalTemplates.length === 0
                    ? renderEmpty(search ? 'No library templates match your search' : 'No library templates available')
                    : activeView === 'grid'
                        ? (
                            <MasonryGrid>
                                {processedGlobalTemplates.map((template: any, idx: number) => {
                                    const key = template.id || template.templateId || `global-${idx}`;
                                    return (
                                        <NewTemplateCard
                                            key={key}
                                            template={template}
                                            isFavorite={favorites.has(String(key))}
                                            onToggleFavorite={() => onToggleFavorite(String(key))}
                                            onPreview={() => handlePreview(template, true)}
                                            onEdit={() => navigate('/templates/editor/new', { state: { content: template.content } })}
                                            onDuplicate={() => { }}
                                            onDelete={() => { }}
                                            isDeletable={false}
                                        />
                                    );
                                })}
                            </MasonryGrid>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {processedGlobalTemplates.map((template: any, idx: number) => {
                                    const key = template.id || template.templateId || `global-${idx}`;
                                    return (
                                        <TemplateRow
                                            key={key}
                                            template={template}
                                            onClick={() => navigate('/templates/editor/new', { state: { content: template.content } })}
                                            onEdit={() => navigate('/templates/editor/new', { state: { content: template.content } })}
                                            onPreview={() => handlePreview(template, true)}
                                            onDuplicate={() => { }}
                                            isDeletable={false}
                                        />
                                    );
                                })}
                            </div>
                        )
                }
            </SkeletonSwitch>


            {/* Preview Modal */}
            {previewTemplate && (
                <TemplatePreviewModal
                    template={previewTemplate}
                    onClose={() => setPreviewTemplate(null)}
                    onEdit={() => {
                        setPreviewTemplate(null);
                        if (previewIsGlobal || !previewTemplate.id) {
                            navigate('/templates/editor/new', { state: { content: previewTemplate.content } });
                        } else {
                            navigate(`/templates/editor/${previewTemplate.id}`);
                        }
                    }}
                />
            )}

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.type === 'delete' ? 'Delete Template' : 'Duplicate Template'}
                message={
                    confirmDialog.type === 'delete'
                        ? `Are you sure you want to delete "${confirmDialog.template?.name}"? This action cannot be undone.`
                        : `Are you sure you want to duplicate "${confirmDialog.template?.name}"? This will create a copy of the template.`
                }
                confirmText={confirmDialog.type === 'delete' ? 'Delete' : 'Duplicate'}
                confirmVariant={confirmDialog.type === 'delete' ? 'danger' : 'primary'}
                isLoading={deleteTemplateMutation.isPending}
                onConfirm={handleConfirmAction}
                onCancel={handleCancelAction}
            />
        </>
    );
};

export default TemplateGrid;
